import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { ImageHistoryEntry } from '../../types/imageGeneration';
import { useImageHistory } from '../../hooks/useImageHistory';
import { imageCacheService } from '../../services/imageCacheService';
import LazyImage from './LazyImage';
import styles from '../../styles/components/image/ImageHistoryDisplay.module.css';

interface ImageHistoryDisplayProps {
  onImageView: (imageUrl: string) => void;
  onHistoryUpdated?: () => void;
}

interface ImageHistoryItemProps {
  entry: ImageHistoryEntry;
  onImageView: (imageUrl: string) => void;
}

const ImageHistoryItem: React.FC<ImageHistoryItemProps> = memo(
  ({ entry, onImageView }) => {
    const [isPromptExpanded, setIsPromptExpanded] = useState(false);
    const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

    // Preload images when component mounts
    useEffect(() => {
      const imageUrls = entry.images.map(img => img.url);
      imageCacheService.preloadImages(imageUrls);
    }, [entry.images]);

    const handleCopyPrompt = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(entry.prompt);
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(null), 2000);
      } catch (error) {
        setCopyFeedback('Failed to copy');
        setTimeout(() => setCopyFeedback(null), 2000);
      }
    }, [entry.prompt]);

    const handleImageClick = useCallback(
      (imageUrl: string) => {
        onImageView(imageUrl);
      },
      [onImageView]
    );

    // Handle keyboard navigation for prompt actions
    const handleActionKeyDown = useCallback(
      (e: React.KeyboardEvent, action: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          action();
        }
      },
      []
    );

    // Memoize formatted values for performance
    const formattedTimestamp = useMemo(() => {
      const now = new Date();
      const diff = now.getTime() - entry.timestamp.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return entry.timestamp.toLocaleDateString();
    }, [entry.timestamp]);

    const formattedSettings = useMemo(() => {
      const parts = [];
      if (entry.settings.selectedModelId) {
        const modelName =
          entry.settings.selectedModelId.split('.')[0] ||
          entry.settings.selectedModelId;
        parts.push(modelName);
      }
      if (entry.settings.imageSize) parts.push(entry.settings.imageSize);
      if (entry.settings.steps) parts.push(`${entry.settings.steps} steps`);
      if (entry.settings.quantity > 1)
        parts.push(`${entry.settings.quantity} images`);
      return parts.join(' • ');
    }, [entry.settings]);

    const truncatedPrompt = useMemo(
      () =>
        entry.prompt.length > 100
          ? entry.prompt.substring(0, 100) + '...'
          : entry.prompt,
      [entry.prompt]
    );

    return (
      <article
        className={styles.historyItem}
        aria-labelledby={`prompt-${entry.id}`}
        aria-describedby={`images-${entry.id} metadata-${entry.id}`}
      >
        <div className={styles.itemRow}>
          {/* Images Grid */}
          <div
            className={styles.imagesGrid}
            id={`images-${entry.id}`}
            role="group"
            aria-label={`Generated images for: ${truncatedPrompt}`}
          >
            {entry.images.map((image, index) => (
              <LazyImage
                key={`${entry.id}-${index}`}
                src={image.url}
                alt={`Generated image ${index + 1} from prompt: ${entry.prompt}`}
                className={styles.thumbnailImage}
                onClick={() => handleImageClick(image.url)}
                loading="lazy"
              />
            ))}
          </div>

          {/* Side panel with prompt and metadata */}
          <div className={styles.sidePanel}>
            <div className={styles.promptSection}>
              <div className={styles.promptText}>
                <p id={`prompt-${entry.id}`} className={styles.promptContent}>
                  {isPromptExpanded ? entry.prompt : truncatedPrompt}
                </p>
                {entry.prompt.length > 100 && (
                  <button
                    className={styles.expandButton}
                    onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                    aria-expanded={isPromptExpanded}
                    aria-controls={`prompt-${entry.id}`}
                  >
                    {isPromptExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
              <div
                className={styles.promptActions}
                role="group"
                aria-label="Prompt actions"
              >
                <button
                  className={styles.actionButton}
                  onClick={handleCopyPrompt}
                  onKeyDown={e => handleActionKeyDown(e, handleCopyPrompt)}
                  title="Copy prompt to clipboard"
                  aria-label="Copy prompt to clipboard"
                >
                  <img
                    src="/icons/chat/copy.svg"
                    alt=""
                    className={styles.actionIcon}
                  />
                  {copyFeedback && (
                    <span className={styles.copyFeedback} aria-live="polite">
                      {copyFeedback}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Metadata Section */}
            <div className={styles.metadataSection} id={`metadata-${entry.id}`}>
              <time
                className={styles.timestamp}
                dateTime={entry.timestamp.toISOString()}
                title={entry.timestamp.toLocaleString()}
              >
                {formattedTimestamp}
              </time>
              <div className={styles.settings} aria-label="Generation settings">
                {formattedSettings}
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  }
);

ImageHistoryItem.displayName = 'ImageHistoryItem';

const ImageHistoryDisplay: React.FC<ImageHistoryDisplayProps> = memo(
  ({ onImageView, onHistoryUpdated }) => {
    const { history, isLoading, error } = useImageHistory();
    const [viewMode, setViewMode] = useState<'recent' | 'feed'>('recent');

    // Flatten images for feed mode, newest first
    const feedImages = useMemo(() => {
      const flattened = history.flatMap(entry =>
        entry.images.map((img, index) => ({
          key: `${entry.id}-${index}`,
          url: img.url,
          timestamp: entry.timestamp.getTime(),
        }))
      );
      return flattened.sort((a, b) => b.timestamp - a.timestamp);
    }, [history]);

    // Memoize history items to prevent unnecessary re-renders
    const memoizedHistoryItems = useMemo(
      () =>
        history.map(entry => (
          <ImageHistoryItem
            key={entry.id}
            entry={entry}
            onImageView={onImageView}
          />
        )),
      [history, onImageView]
    );

    // Notify parent when history updates
    useEffect(() => {
      if (onHistoryUpdated) onHistoryUpdated();
    }, [history, onHistoryUpdated]);

    // Error State
    if (error) {
      return (
        <section className={styles.container} role="alert" aria-live="polite">
          <div className={styles.errorCard}>
            <p className={styles.errorText}>{error}</p>
          </div>
        </section>
      );
    }

    // Loading State
    if (isLoading && history.length === 0) {
      return (
        <section
          className={styles.container}
          aria-live="polite"
          aria-busy="true"
        >
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner} aria-hidden="true"></div>
            <p className={styles.loadingText}>Loading image history...</p>
          </div>
        </section>
      );
    }

    // History List / Feed
    return (
    <section className={styles.container}>
      <div className={styles.toggleHeader}>
        <div
          className={styles.toggleBar}
          role="tablist"
          aria-label="History view toggle"
        >
          <button
            role="tab"
            aria-selected={viewMode === 'recent'}
            className={
              viewMode === 'recent'
                ? `${styles.toggleButton} ${styles.toggleButtonActive}`
                : styles.toggleButton
            }
            onClick={() => setViewMode('recent')}
          >
            Recent
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'feed'}
            className={
              viewMode === 'feed'
                ? `${styles.toggleButton} ${styles.toggleButtonActive}`
                : styles.toggleButton
            }
            onClick={() => setViewMode('feed')}
          >
            Feed
          </button>
        </div>
      </div>

        {viewMode === 'recent' ? (
          <>
            {history.length === 0 ? (
              <div className={styles.emptyCard}>
                <div className={styles.emptyContent}>
                  <div
                    className={styles.emptyIcon}
                    aria-hidden="true"
                    style={{
                      ['--empty-icon-mask' as any]:
                        "url('/icons/image-placeholder.svg') no-repeat center / contain",
                    }}
                  />
                  <h3 className={styles.emptyTitle}>No images generated yet</h3>
                  <p className={styles.emptyDescription}>
                    Your generated images will appear here. Start by entering a
                    prompt below.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="sr-only">Image Generation History</h2>
                <div
                  className={styles.historyList}
                  role="feed"
                  aria-label={`${history.length} image generation${history.length === 1 ? '' : 's'} in history`}
                >
                  {memoizedHistoryItems}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {feedImages.length === 0 ? (
              <div className={styles.emptyCard}>
                <div className={styles.emptyContent}>
                  <div
                    className={styles.emptyIcon}
                    aria-hidden="true"
                    style={{
                      ['--empty-icon-mask' as any]:
                        "url('/icons/image-placeholder.svg') no-repeat center / contain",
                    }}
                  />
                  <h3 className={styles.emptyTitle}>No images yet</h3>
                  <p className={styles.emptyDescription}>
                    Switch to Recent to see generation details once you start
                    creating images.
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={styles.feedGrid}
                role="grid"
                aria-label={`${feedImages.length} images`}
              >
                {feedImages.map(img => (
                  <LazyImage
                    key={img.key}
                    src={img.url}
                    alt="Generated image"
                    className={styles.feedImage}
                    onClick={() => onImageView(img.url)}
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    );
  }
);

ImageHistoryDisplay.displayName = 'ImageHistoryDisplay';

export default ImageHistoryDisplay;
